import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { routes, getEprBaseUrl } from '../routes';
import { fetchFormsForReviewer, fetchReviewForm, saveManagerReviewDraft, submitManagerReview, transitionStatus } from '../services/reviewFormService';
import { fetchTemplate } from '../services/reviewTemplateService';
import { fetchCycle } from '../services/reviewCycleService';
import { fetchResponsesForForm, aggregate360Scores, generateInvites, fetchInvitesForForm } from '../services/review360Service';
import { computeFormScores, aggregateQuestionScores } from '../services/scoreEngine';
import ScoreBar from '../components/ScoreBar';
import WorkflowStatusBadge from '../components/WorkflowStatusBadge';
import type { ReviewForm, ReviewTemplate, Review360Invite } from '../types';

const GAP_THRESHOLD = 1.5;

const ManagerReviewForm: React.FC = () => {
  const { reviewFormId } = useParams<{ reviewFormId?: string }>();
  const { user } = useAuth();

  // List mode
  const [forms, setForms] = useState<ReviewForm[]>([]);
  // Detail mode
  const [form, setForm] = useState<ReviewForm | null>(null);
  const [template, setTemplate] = useState<ReviewTemplate | null>(null);
  const [invites, setInvites] = useState<Review360Invite[]>([]);
  const [avg360, setAvg360] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  // Manager review state
  const [krScores, setKrScores] = useState<{ krId: string; managerScore: number; notes: string }[]>([]);
  const [kpiScores, setKpiScores] = useState<{ tagId: string; managerScore: number }[]>([]);
  const [opticsQScores, setOpticsQScores] = useState<Record<string, number[]>>({});
  const [characterScores, setCharacterScores] = useState<Record<string, number>>({});
  const [overallNotes, setOverallNotes] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [invite360Count, setInvite360Count] = useState(3);
  const [generatedLinks, setGeneratedLinks] = useState<{ token: string; url: string }[]>([]);
  const [section, setSection] = useState<'kr' | 'optics' | 'character' | 'submit'>('kr');

  const loadDetail = useCallback(async (fid: string) => {
    const f = await fetchReviewForm(fid);
    if (!f) return;
    setForm(f);

    const cycle = await fetchCycle(f.cycleId);
    if (cycle) {
      const t = await fetchTemplate(cycle.templateId);
      setTemplate(t);
      if (t) {
        // Init manager review state from existing draft or defaults
        const mr = f.managerReview;
        setKrScores(
          mr.krScores && mr.krScores.length
            ? mr.krScores
            : (f.selfReview.krProgress ?? []).map(k => ({ krId: k.krId, managerScore: 0, notes: '' })),
        );
        setKpiScores(
          mr.kpiScores && mr.kpiScores.length
            ? mr.kpiScores
            : (f.selfReview.kpiEvidence ?? []).map(k => ({ tagId: k.tagId, managerScore: 0 })),
        );
        if (mr.opticsScores && Object.keys(mr.opticsScores).length) {
          // Convert aggregate back to question arrays (approximate: repeat)
          const init: Record<string, number[]> = {};
          t.opticsCriteria.forEach(c => {
            const agg = mr.opticsScores![c.name] ?? 0;
            init[c.name] = Array(c.questions.length).fill(agg > 0 ? Math.round(agg) : 0);
          });
          setOpticsQScores(init);
        } else {
          const init: Record<string, number[]> = {};
          t.opticsCriteria.forEach(c => { init[c.name] = Array(c.questions.length).fill(0); });
          setOpticsQScores(init);
        }
        setCharacterScores(mr.characterScores ?? {});
        setOverallNotes(mr.overallNotes ?? '');
      }
    }

    // 360 data
    const invs = await fetchInvitesForForm(fid);
    setInvites(invs);
    const responses = await fetchResponsesForForm(fid);
    setAvg360(aggregate360Scores(responses));
  }, []);

  useEffect(() => {
    const init = async () => {
      if (!user) return;
      try {
        if (reviewFormId) {
          await loadDetail(reviewFormId);
        } else {
          const list = await fetchFormsForReviewer(user.id);
          setForms(list);
        }
      } catch (e) {
        console.error('Failed to load manager review', e);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [user, reviewFormId, loadDetail]);

  if (loading) return <div className="flex items-center justify-center min-h-[60vh] text-blue-600">Loading…</div>;

  // ─── List mode ─────────────────────────────────────────────────────────────

  if (!reviewFormId) {
    return (
      <div className="max-w-6xl mx-auto p-6 space-y-4">
        <h1 className="text-2xl font-bold text-gray-900">Manager Review</h1>
        {forms.length === 0 && <p className="text-gray-400 text-center py-10">No review forms assigned to you.</p>}
        {forms.map(f => (
          <a
            key={f.id}
            href={`/manager-review/${f.id}`}
            className="flex items-start justify-between p-4 bg-white border border-gray-200 shadow-sm rounded-xl hover:border-blue-400 transition gap-4"
          >
            <div>
              <p className="font-semibold text-gray-900">{f.revieweeName}</p>
              <p className="text-xs text-gray-500 mt-0.5">Cycle: {f.cycleId}</p>
            </div>
            <WorkflowStatusBadge status={f.status} size="sm" />
          </a>
        ))}
      </div>
    );
  }

  if (!form || !template) return <div className="p-6 text-gray-500">Form not found.</div>;

  // ─── Detail mode ───────────────────────────────────────────────────────────

  const selfKr = form.selfReview.krProgress ?? [];
  const selfOptics = form.selfReview.opticsScores ?? {};
  const selfChar = form.selfReview.characterScores ?? {};

  const handleSaveDraft = async () => {
    if (!form || !template) return;
    setSaving(true);
    setError('');
    try {
      const opticsAgg: Record<string, number> = {};
      template.opticsCriteria.forEach(c => {
        opticsAgg[c.name] = aggregateQuestionScores(opticsQScores[c.name] ?? []);
      });
      await saveManagerReviewDraft(form.id, { krScores, kpiScores, opticsScores: opticsAgg, characterScores, overallNotes });
    } catch (e) {
      setError('Failed to save draft: ' + String(e));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!form || !template || !user) return;
    const missing: string[] = [];
    if (krScores.some(k => k.managerScore === 0)) missing.push('KR scores');
    if (template.opticsCriteria.some(c => (opticsQScores[c.name] ?? []).some(s => s === 0))) missing.push('OPTICS scores');
    if (template.characterCriteria.some(c => !characterScores[c.name])) missing.push('Character scores');
    if (missing.length) { setError(`Complete: ${missing.join(', ')}`); return; }
    setError('');
    setSaving(true);
    try {
      const opticsAgg: Record<string, number> = {};
      template.opticsCriteria.forEach(c => {
        opticsAgg[c.name] = aggregateQuestionScores(opticsQScores[c.name] ?? []);
      });
      const reviewData = { krScores, kpiScores, opticsScores: opticsAgg, characterScores, overallNotes };

      // Get team scorecard score (default 3 if not linked)
      const teamScore = 3; // TODO: fetch actual team scorecard
      const scores = computeFormScores(form.selfReview, reviewData, teamScore, template);

      await submitManagerReview(form.id, reviewData, scores.totalScore, user.id);
      await loadDetail(form.id);
    } catch (e) {
      setError(String(e));
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate360 = async () => {
    if (!form) return;
    setSaving(true);
    setError('');
    try {
      const baseUrl = getEprBaseUrl();
      const links = await generateInvites(form.id, form.revieweeId, form.revieweeName, form.cycleId, invite360Count, baseUrl);
      setGeneratedLinks(links);
      await transitionStatus(form.id, '360_pending', user!.id);
      await loadDetail(form.id);
    } catch (e) {
      setError('Failed to generate 360 invites: ' + String(e));
    } finally {
      setSaving(false);
    }
  };

  const SECTIONS = [
    { id: 'kr' as const, label: 'KR & KPI' },
    { id: 'optics' as const, label: 'OPTICS' },
    { id: 'character' as const, label: 'Character' },
    { id: 'submit' as const, label: 'Submit' },
  ];

  const gapColor = (self: number, manager: number) => {
    const gap = Math.abs(manager - self);
    if (gap >= GAP_THRESHOLD) return 'text-red-600 font-bold';
    if (gap >= 1) return 'text-brand-warning';
    return 'text-emerald-600';
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <a href="/manager-review" className="text-xs text-gray-500 hover:text-gray-900">← All Reviews</a>
          <h1 className="text-2xl font-bold text-gray-900 mt-1">{form.revieweeName}</h1>
          <p className="text-gray-500 text-sm">Cycle: {form.cycleId}</p>
        </div>
        <WorkflowStatusBadge status={form.status} />
      </div>

      {/* 360 management */}
      <div className="bg-white border border-gray-200 shadow-sm rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-gray-900">360° Feedback</p>
          <span className="text-xs text-gray-500">{invites.filter(i => i.status === 'completed').length}/{invites.length} responses</span>
        </div>
        {invites.length === 0 ? (
          <div className="flex items-center gap-3 flex-wrap">
            <input
              type="number"
              min={1}
              max={20}
              value={invite360Count}
              onChange={e => setInvite360Count(Number(e.target.value))}
              className="w-20 bg-white text-gray-900 text-sm border border-gray-200 rounded px-2 py-1"
            />
            <button onClick={handleGenerate360} className="bg-brand-accent/20 border border-brand-accent/40 text-blue-600 text-xs px-3 py-1.5 rounded hover:bg-brand-accent/30 transition">
              Generate {invite360Count} Invite Links
            </button>
          </div>
        ) : (
          <div className="space-y-1.5">
            {invites.map(inv => (
              <div key={inv.id} className="flex items-center gap-2 text-xs text-gray-500">
                <span>{inv.status === 'completed' ? '✅' : '⏳'}</span>
                <code className="bg-white rounded px-2 py-0.5 text-gray-700 font-mono text-xs flex-1 truncate">
                  {routes.public360InviteUrl(inv.token)}
                </code>
                <button onClick={() => navigator.clipboard.writeText(routes.public360InviteUrl(inv.token))} className="text-blue-600 hover:underline">Copy</button>
              </div>
            ))}
          </div>
        )}
        {generatedLinks.length > 0 && (
          <div className="mt-2 space-y-1">
            {generatedLinks.map(l => (
              <div key={l.token} className="flex items-center gap-2">
                <input value={l.url} readOnly className="flex-1 bg-gray-100 text-gray-700 border border-gray-300 text-xs rounded px-2 py-1" />
                <button onClick={() => navigator.clipboard.writeText(l.url)} className="text-blue-600 text-xs hover:underline">Copy</button>
              </div>
            ))}
          </div>
        )}
        {Object.keys(avg360).length > 0 && (
          <p className="text-xs text-gray-500">
            360 avg OPTICS: {Object.entries(avg360).map(([k, v]) => `${k}: ${v.toFixed(1)}`).join(' · ')}
          </p>
        )}
      </div>

      {/* Section tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {SECTIONS.map(s => (
          <button key={s.id} onClick={() => setSection(s.id)}
            className={`px-4 py-2 text-xs font-medium border-b-2 -mb-px transition ${section === s.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-900'}`}>
            {s.label}
          </button>
        ))}
      </div>

      {/* KR + KPI */}
      {section === 'kr' && (
        <div className="space-y-4">
          <p className="text-xs text-gray-500 italic">Gap ≥{GAP_THRESHOLD} highlighted in red. Values shown: <span className="text-gray-700">Self → Manager</span>.</p>
          {selfKr.map((kr, idx) => {
            const ms = krScores.find(k => k.krId === kr.krId) ?? { krId: kr.krId, managerScore: 0, notes: '' };
            const gap = ms.managerScore > 0 ? Math.abs(ms.managerScore - kr.selfScore) : 0;
            return (
              <div key={kr.krId} className={`bg-white border rounded-xl p-4 space-y-3 shadow-sm ${gap >= GAP_THRESHOLD && ms.managerScore > 0 ? 'border-red-300' : 'border-gray-200'}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <span className="text-xs text-gray-400">{kr.krNumber || `KR ${idx + 1}`}</span>
                    <h3 className="font-medium text-gray-900 text-sm">{kr.krTitle}</h3>
                    <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{kr.evidence}</p>
                  </div>
                  {ms.managerScore > 0 && (
                    <div className="text-right shrink-0">
                      <span className={gapColor(kr.selfScore, ms.managerScore)}>GAP: {(ms.managerScore - kr.selfScore).toFixed(0)}</span>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Self Score</p>
                    <div className="flex gap-1.5">
                      {[1,2,3,4,5].map(n => (
                        <div key={n} className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${kr.selfScore === n ? 'bg-blue-100 text-blue-700' : 'bg-transparent text-gray-400'}`}>{n}</div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Manager Score</p>
                    <div className="flex gap-1.5">
                      {[1,2,3,4,5].map(n => (
                        <button key={n} type="button"
                          onClick={() => setKrScores(prev => prev.map(k => k.krId === kr.krId ? { ...k, managerScore: n } : k))}
                          className={`w-8 h-8 rounded-full text-xs font-semibold border transition ${ms.managerScore === n ? 'bg-brand-accent border-blue-600 text-blue-600' : 'bg-transparent border-gray-300 text-gray-400 hover:border-white'}`}
                        >{n}</button>
                      ))}
                    </div>
                  </div>
                </div>
                <textarea
                  value={ms.notes}
                  onChange={e => setKrScores(prev => prev.map(k => k.krId === kr.krId ? { ...k, notes: e.target.value } : k))}
                  placeholder="Manager notes (optional)…"
                  rows={2}
                  className="w-full bg-white text-gray-900 text-xs border border-gray-200 rounded-lg px-3 py-2 focus:border-brand-accent focus:outline-none resize-none"
                />
              </div>
            );
          })}
        </div>
      )}

      {/* OPTICS */}
      {section === 'optics' && (
        <div className="space-y-5">
          {template.opticsCriteria.map(criteria => {
            const qScores = opticsQScores[criteria.name] ?? [];
            const selfAgg = selfOptics[criteria.name] ?? 0;
            const managerAgg = aggregateQuestionScores(qScores);
            const gap = managerAgg > 0 ? Math.abs(managerAgg - selfAgg) : 0;
            return (
              <div key={criteria.name} className={`bg-white border rounded-xl overflow-hidden shadow-sm ${gap >= GAP_THRESHOLD && managerAgg > 0 ? 'border-red-300' : 'border-gray-200'}`}>
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900 text-sm">{criteria.name} · {Math.round(criteria.weight * 100)}%</h3>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-gray-500">Self: <strong className="text-gray-900">{selfAgg > 0 ? selfAgg.toFixed(1) : '—'}</strong></span>
                    <span className="text-gray-500">Manager: <strong className="text-gray-900">{managerAgg > 0 ? managerAgg.toFixed(1) : '—'}</strong></span>
                    {gap >= GAP_THRESHOLD && managerAgg > 0 && <span className="text-red-600 font-bold">GAP ⚠️</span>}
                  </div>
                </div>
                <div className="p-4 space-y-4">
                  {avg360[criteria.name] != null && (
                    <div className="flex items-center gap-2 text-xs text-purple-700 bg-purple-50 rounded p-2">
                      <span>👥 360° avg: <strong>{avg360[criteria.name].toFixed(1)}</strong></span>
                    </div>
                  )}
                  {criteria.questions.map((q, qi) => (
                    <div key={qi}>
                      <p className="text-sm text-gray-700 mb-1.5">{q}</p>
                      <div className="flex gap-1.5">
                        {[1,2,3,4,5].map(n => (
                          <button key={n} type="button"
                            onClick={() => {
                              const updated = [...(opticsQScores[criteria.name] ?? [])];
                              updated[qi] = n;
                              setOpticsQScores({ ...opticsQScores, [criteria.name]: updated });
                            }}
                            className={`w-8 h-8 rounded-full text-xs font-semibold border transition ${qScores[qi] === n ? 'bg-brand-accent border-blue-600 text-blue-600' : 'bg-transparent border-gray-300 text-gray-400 hover:border-white'}`}
                          >{n}</button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Character */}
      {section === 'character' && (
        <div className="space-y-4">
          {template.characterCriteria.map(criteria => {
            const selfScore = selfChar[criteria.name] ?? 0;
            const managerScore = characterScores[criteria.name] ?? 0;
            const gap = managerScore > 0 ? Math.abs(managerScore - selfScore) : 0;
            return (
              <div key={criteria.name} className={`bg-white border rounded-xl p-4 space-y-3 shadow-sm ${gap >= GAP_THRESHOLD && managerScore > 0 ? 'border-red-300' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900 text-sm">{criteria.name}</h3>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span>Self: <strong className="text-gray-900">{selfScore || '—'}</strong></span>
                    <span>Manager: <strong className="text-gray-900">{managerScore || '—'}</strong></span>
                  </div>
                </div>
                <div className="space-y-1">
                  {[1,2,3,4,5].map(n => (
                    <button key={n} type="button"
                      onClick={() => setCharacterScores({ ...characterScores, [criteria.name]: n })}
                      className={`w-full text-left p-2 rounded border transition text-xs ${managerScore === n ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-400 text-gray-500'}`}
                    >
                      <span className="font-semibold text-gray-700 mr-2">{n}</span>{criteria.rubric[n] || '—'}
                    </button>
                  ))}
                </div>
                {managerScore > 0 && <ScoreBar score={managerScore} showValue={false} size="sm" />}
              </div>
            );
          })}
          <div>
            <label className="text-xs text-gray-500 block mb-1">Overall Manager Notes</label>
            <textarea
              value={overallNotes}
              onChange={e => setOverallNotes(e.target.value)}
              rows={4}
              placeholder="Overall assessment and development recommendations…"
              className="w-full bg-white text-gray-900 text-sm border border-gray-200 rounded-xl px-3 py-2 focus:border-brand-accent focus:outline-none resize-none"
            />
          </div>
        </div>
      )}

      {/* Submit */}
      {section === 'submit' && (
        <div className="space-y-4">
          <p className="text-gray-500 text-sm">Review your inputs before submitting. Once submitted the form moves to Calibration.</p>
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <div className="flex gap-3">
            <button onClick={handleSubmit} disabled={saving} className="bg-brand-success hover:opacity-90 text-gray-900 font-semibold px-6 py-2.5 rounded-lg text-sm transition disabled:opacity-50">
              {saving ? 'Submitting…' : 'Submit Manager Review'}
            </button>
            <button onClick={handleSaveDraft} disabled={saving} className="border border-gray-300 text-gray-700 hover:text-gray-900 px-5 py-2.5 text-sm rounded-lg transition">
              Save Draft
            </button>
          </div>
        </div>
      )}

      {section !== 'submit' && (
        <div className="flex justify-end">
          <button onClick={handleSaveDraft} disabled={saving} className="text-xs text-gray-500 hover:text-gray-900 border border-gray-200 rounded px-3 py-1.5">
            {saving ? 'Saving…' : '💾 Save Draft'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ManagerReviewForm;
